import {
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Box,
  StepConnector,
  stepConnectorClasses,
  Fade,
  Tooltip,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { STATUS_STEPS, getStatusColor } from "./constants";

// Pulse animation for active step
const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.7);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 12px rgba(25, 118, 210, 0);
    transform: scale(1.05);
  }
`;

// Custom styled stepper connector
const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 28,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        "linear-gradient(95deg, #1976d2 0%, #0288d1 50%, #01579b 100%)",
      boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        "linear-gradient(95deg, #2e7d32 0%, #43a047 50%, #66bb6a 100%)",
      boxShadow: "0 2px 8px rgba(46, 125, 50, 0.3)",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 4,
    border: 0,
    backgroundColor:
      theme.palette.mode === "dark" ? theme.palette.grey[800] : "#e0e0e0",
    borderRadius: 2,
    transition: "all 0.3s ease",
  },
}));

const OrderProgressStepper = ({
  currentStatus,
  hasPaused = false,
  hasUnloaded = false,
  progressStatesWithDates = {},
  progressStates = [],
}) => {
  const isCanceled = currentStatus === "Canceled";

  // Determine if Unloaded comes before or after Ready for Delivery
  const unloadIndex = progressStates.findIndex((state) => state === "Unloaded");
  const readyForDeliveryIndex = progressStates.findIndex(
    (state) => state === "Ready for Delivery"
  );
  const unloadedAfterReady =
    hasUnloaded &&
    unloadIndex !== -1 &&
    readyForDeliveryIndex !== -1 &&
    unloadIndex > readyForDeliveryIndex;

  // Create dynamic steps order based on unload position
  let orderedSteps = [...STATUS_STEPS];
  if (unloadedAfterReady) {
    // Move Unloaded to after Ready for Delivery
    const unloadedStep = orderedSteps.find((step) => step.label === "Unloaded");
    orderedSteps = orderedSteps.filter((step) => step.label !== "Unloaded");
    const readyIndex = orderedSteps.findIndex(
      (step) => step.label === "Ready for Delivery"
    );
    orderedSteps.splice(readyIndex + 1, 0, unloadedStep);
  }

  // Filter steps based on whether order has paused or unloaded states
  const filteredSteps = orderedSteps.filter((step) => {
    if (step.label === "Paused" && !hasPaused) return false;
    if (step.label === "Unloaded" && !hasUnloaded) return false;

    // If order is canceled, show steps up to In Transit and then Canceled
    if (isCanceled) {
      if (step.label === "Delivered") return false;
    } else {
      // If order is not canceled, don't show Canceled step
      if (step.label === "Canceled") return false;
    }

    return true;
  });

  const getActiveStep = (status) => {
    const index = filteredSteps.findIndex((step) => step.label === status);
    return index;
  };

  const activeStep = getActiveStep(currentStatus);

  return (
    <Fade in={true} timeout={1000}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 3,
          borderRadius: "16px",
          border: "1px solid #e0e0e0",
          transition: "all 0.3s ease",
          backgroundColor: "#fafafa",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            borderColor: "primary.light",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight="700" color="primary">
            Order Progress
          </Typography>
          {isCanceled && (
            <Box
              sx={{
                px: 2,
                py: 0.5,
                borderRadius: "12px",
                backgroundColor: "error.50",
                border: `1px solid error.main`,
              }}
            >
              <Typography variant="caption" fontWeight="600" color="error.main">
                {currentStatus}
              </Typography>
            </Box>
          )}
        </Box>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          connector={<CustomConnector />}
          sx={{
            transition: "opacity 0.3s ease",
          }}
        >
          {filteredSteps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = index === activeStep;
            const isCompleted = index < activeStep;
            const stepColor =
              isCompleted || isActive ? getStatusColor(step.label) : "#bdbdbd";

            return (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Tooltip
                      title={
                        isCompleted
                          ? `${step.label} - Completed`
                          : isActive
                          ? `${step.label} - Current`
                          : `${step.label} - Upcoming`
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          position: "relative",
                          zIndex: 1,
                          width: isActive ? 64 : 56,
                          height: isActive ? 64 : 56,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: stepColor,
                          color: "white",
                          border: isActive
                            ? "4px solid white"
                            : "3px solid white",
                          boxShadow: isActive
                            ? `0 0 0 4px ${stepColor}33, 0 4px 12px rgba(0,0,0,0.15)`
                            : isCompleted
                            ? "0 2px 8px rgba(0,0,0,0.1)"
                            : "0 1px 4px rgba(0,0,0,0.08)",
                          animation: isActive
                            ? `${pulse} 2s ease-in-out infinite`
                            : "none",
                          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          cursor: "pointer",
                          "&:hover": {
                            transform: "scale(1.1)",
                            boxShadow: `0 0 0 6px ${stepColor}22, 0 6px 16px rgba(0,0,0,0.2)`,
                          },
                        }}
                      >
                        <StepIcon sx={{ fontSize: isActive ? 32 : 28 }} />
                      </Box>
                    </Tooltip>
                  )}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1.5,
                        fontWeight: isActive
                          ? "bold"
                          : isCompleted
                          ? 600
                          : "normal",
                        color:
                          isActive || isCompleted
                            ? "text.primary"
                            : "text.secondary",
                        fontSize: isActive ? "0.9rem" : "0.85rem",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {step.label}
                    </Typography>
                    {progressStatesWithDates[step.label] && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.5,
                          color:
                            isActive || isCompleted
                              ? "text.secondary"
                              : "text.disabled",
                          fontSize: "0.7rem",
                          fontWeight: isActive ? 600 : 400,
                          transition: "all 0.3s ease",
                        }}
                      >
                        {progressStatesWithDates[step.label]}
                      </Typography>
                    )}
                  </Box>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Paper>
    </Fade>
  );
};

export default OrderProgressStepper;
